import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  applyUserLanguagePreference,
  applyUserTourProgress,
  setShiftsDefaultView,
  type TourProgressMap,
} from '../utils/userPreferences';

interface User {
  id: number;
  email: string;
  username: string;
  role: 'OWNER' | 'EMPLOYEE' | 'MANAGER' | 'SUPER_ADMIN';
  firstName: string;
  lastName: string;
  address?: string;
  phone?: string;
  avatarUrl?: string;
  businessId?: number;
  managerBusinesses?: { businessId: number }[];
  workplaces?: { businessId: number }[];
  /** Per-user preference - not shared across the business */
  shiftsDefaultView?: 'list' | 'calendar';
  /** Per-user preference - not shared across the business */
  preferredLanguage?: 'cs' | 'en';
  /** Product tour completion map */
  tourProgress?: TourProgressMap;
  /** Set when a SUPER_ADMIN is viewing as another user */
  impersonating?: boolean;
  impersonatorId?: number | null;
  /** Session-only Firma Ompli demo sandbox (no DB persistence) */
  isDemo?: boolean;
  demoActorKey?: string;
}

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  const hydrateFromApiOrStorage = () => {
    fetch('/api/users/me', { credentials: 'include' })
      .then(async (res) => {
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('user');
          setUser(null);
          return;
        }
        if (!res.ok) throw new Error('Failed to load user');
        const data = await res.json();
        if (!data?.id || !data?.role) throw new Error('Invalid user payload');
        setUser({
          ...data,
          businessId: data.businessId || null,
        });
        localStorage.setItem('user', JSON.stringify(data));
        if (data?.id && (data.shiftsDefaultView === 'list' || data.shiftsDefaultView === 'calendar')) {
          setShiftsDefaultView(data.shiftsDefaultView, data.id);
        }
        applyUserLanguagePreference(data);
        applyUserTourProgress(data);
      })
      .catch(() => {
        // Network / 5xx: keep cached user for offline UX only
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
          setUser(null);
          return;
        }
        try {
          const parsed = JSON.parse(storedUser);
          if (parsed?.id && parsed?.role) setUser(parsed);
          else {
            localStorage.removeItem('user');
            setUser(null);
          }
        } catch {
          localStorage.removeItem('user');
          setUser(null);
        }
      });
  };

  useEffect(() => {
    hydrateFromApiOrStorage();
    window.addEventListener('userUpdated', hydrateFromApiOrStorage);
    return () => window.removeEventListener('userUpdated', hydrateFromApiOrStorage);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 